import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Edit, ChevronRight, Truck, Package, Search, ArrowUpDown, Boxes, Archive, ArchiveRestore, TrendingUp } from "lucide-react";
import { DisputeBadge } from "@/components/admin/DisputeBadge";
import { toast } from "sonner";
import SupplierFormDialog from "../components/suppliers/SupplierFormDialog";
import SupplierDetailView from "../components/suppliers/SupplierDetailView";
import StockForecastTab from "../components/suppliers/StockForecastTab";
import { SupplierAnalyticsPage } from "./SupplierAnalyticsPage";
import { useTableColumns } from "@/hooks/useTableColumns";
import { ColumnsToggle, SortButton } from "@/components/TableControls";

const SUPPLIER_COLUMNS = [
  { key: "company_name", label: "Fournisseur" },
  { key: "category", label: "Catégorie" },
  { key: "phone", label: "Téléphone" },
  { key: "email", label: "Email" },
  { key: "contacts", label: "Contacts" },
  { key: "product_count", label: "Produits" },
  { key: "actions", label: "Actions", sortable: false, always: true },
];

const SuppliersList = ({ onSelect, onEdit }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesById, setCategoriesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const {
    isVisible: isSupColVisible,
    toggleVisibility: toggleSupCol,
    handleSort: handleSupSort,
    sortArrow: supSortArrow,
    sortItems: sortSups,
    orderedColumns: orderedSupColumns,
    moveColumn: moveSupColumn,
    resetOrder: resetSupOrder,
  } = useTableColumns({ storageKey: "suppliers-table", columns: SUPPLIER_COLUMNS });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { include_archived: showArchived };
      if (categoryFilter && categoryFilter !== "all") params.category_id = categoryFilter;
      const [{ data }, catRes] = await Promise.all([
        api.getSuppliers(params),
        api.getSupplierCategories().catch(() => ({ data: [] })),
      ]);
      setSuppliers(data);
      const cats = catRes.data || [];
      setCategories(cats);
      const map = {};
      for (const c of cats) map[c.id] = c;
      setCategoriesById(map);
    } catch (_e) {
      toast.error("Erreur chargement fournisseurs");
    } finally {
      setLoading(false);
    }
  }, [showArchived, categoryFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleArchiveToggle = async (sup) => {
    try {
      if (sup.archived) {
        await api.unarchiveSupplier(sup.id);
        toast.success(`${sup.company_name} réactivé`);
      } else {
        await api.archiveSupplier(sup.id);
        toast.success(`${sup.company_name} archivé`);
      }
      fetchData();
    } catch {
      toast.error("Erreur archivage");
    }
  };

  const filtered = useMemo(() => {
    if (!search) return suppliers;
    const s = search.toLowerCase();
    return suppliers.filter(x =>
      (x.company_name || "").toLowerCase().includes(s) ||
      (x.siret || "").toLowerCase().includes(s) ||
      (x.email || "").toLowerCase().includes(s)
    );
  }, [suppliers, search]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (nom, SIRET, email)..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9" data-testid="supplier-search"
          />
        </div>
        <div className="min-w-[200px]">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="supplier-category-filter" className="h-9">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              <SelectItem value="none">— Sans catégorie —</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`category-filter-option-${c.id}`}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-archived-suppliers-toggle">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4" />
          Afficher archivés
        </label>
      </div>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">
              {suppliers.length === 0
                ? "Aucun fournisseur. Cliquez sur 'Nouveau fournisseur' pour commencer."
                : "Aucun résultat."}
            </p>
          ) : (
            <>
            <div className="flex items-center justify-end px-4 py-2 border-b bg-slate-50/50">
              <ColumnsToggle columns={SUPPLIER_COLUMNS} orderedColumns={orderedSupColumns} isVisible={isSupColVisible} toggleVisibility={toggleSupCol} moveColumn={moveSupColumn} resetOrder={resetSupOrder} testId="suppliers-columns-toggle" />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  {orderedSupColumns.filter(c => isSupColVisible(c.key)).map(col => {
                    if (col.key === 'actions') return <TableHead key={col.key}>Actions</TableHead>;
                    if (col.key === 'contacts') return <TableHead key={col.key}>Contacts</TableHead>;
                    if (col.key === 'category') return <TableHead key={col.key}>Catégorie</TableHead>;
                    return (
                      <TableHead key={col.key}>
                        <SortButton columnKey={col.key} label={col.label} sortArrow={supSortArrow} handleSort={handleSupSort} />
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortSups(filtered, {
                  contacts: (s) => (s.contacts || []).length,
                }).map(s => {
                  const renderSupCell = (key) => {
                    switch(key) {
                      case 'company_name': return (
                        <TableCell key={key}>
                          <div className="flex items-center gap-2">
                            <button onClick={() => onSelect(s)} className="text-primary hover:underline text-left font-medium" data-testid={`supplier-name-${s.id}`}>
                              {s.company_name}
                            </button>
                            <DisputeBadge thirdPartyId={s.id} />
                            {s.archived && (
                              <Badge variant="outline" className="text-[10px] border-slate-400 text-slate-600" data-testid={`archived-badge-supplier-${s.id}`}>
                                Archivé
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      );
                      case 'phone': return <TableCell key={key} className="font-mono text-sm">{s.phone || "-"}</TableCell>;
                      case 'category': {
                        const cat = s.category_id ? categoriesById[s.category_id] : null;
                        return (
                          <TableCell key={key}>
                            {cat ? (
                              <Badge
                                variant="outline"
                                className="text-xs"
                                style={{ borderColor: cat.color, color: cat.color }}
                                data-testid={`supplier-category-badge-${s.id}`}
                              >
                                {cat.name}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        );
                      }
                      case 'email': return <TableCell key={key} className="text-sm">{s.email || "-"}</TableCell>;
                      case 'contacts': return <TableCell key={key}>{(s.contacts || []).length}</TableCell>;
                      case 'product_count': return <TableCell key={key}><Badge variant="outline">{s.product_count || 0}</Badge></TableCell>;
                      case 'actions': return (
                        <TableCell key={key}>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => onSelect(s)} data-testid={`view-supplier-${s.id}`}>
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => onEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleArchiveToggle(s)} data-testid={`archive-supplier-${s.id}`} title={s.archived ? "Restaurer" : "Archiver"}>
                              {s.archived ? <ArchiveRestore className="h-4 w-4 text-emerald-600" /> : <Archive className="h-4 w-4 text-slate-500" />}
                            </Button>
                          </div>
                        </TableCell>
                      );
                      default: return null;
                    }
                  };
                  return (
                    <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                      {orderedSupColumns.filter(col => isSupColVisible(col.key)).map(col => renderSupCell(col.key))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ProductsCatalog = () => {
  const [products, setProducts] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [filterOrderType, setFilterOrderType] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, otRes, sRes] = await Promise.all([
        api.getAllProducts(),
        api.getOrderTypes(),
        api.getSuppliers(),
      ]);
      setProducts(pRes.data);
      setOrderTypes(otRes.data);
      setSuppliers(sRes.data);
    } catch (_e) {
      toast.error("Erreur chargement catalogue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    let out = products;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(p =>
        (p.name || "").toLowerCase().includes(s) ||
        (p.reference || "").toLowerCase().includes(s) ||
        (p.supplier_name || "").toLowerCase().includes(s)
      );
    }
    if (filterSupplier !== "all") out = out.filter(p => p.supplier_id === filterSupplier);
    if (filterOrderType !== "all") out = out.filter(p => (p.order_type_ids || []).includes(filterOrderType));
    if (filterType !== "all") out = out.filter(p => p.product_type === filterType);

    out = [...out].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = a[sortBy] ?? "";
      const bv = b[sortBy] ?? "";
      if (sortBy === "price_per_reference_unit" || sortBy === "price_per_usage_unit") {
        const an = a[sortBy] ?? Number.POSITIVE_INFINITY;
        const bn = b[sortBy] ?? Number.POSITIVE_INFINITY;
        return (an - bn) * dir;
      }
      return String(av).localeCompare(String(bv), "fr", { numeric: true }) * dir;
    });
    return out;
  }, [products, search, filterSupplier, filterOrderType, filterType, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortHeader = ({ label, col }) => (
    <TableHead>
      <button onClick={() => toggleSort(col)} className="inline-flex items-center gap-1 hover:text-primary" data-testid={`sort-${col}`}>
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortBy === col ? "text-primary" : "opacity-50"}`} />
      </button>
    </TableHead>
  );

  const uniqueTypes = useMemo(() => {
    return Array.from(new Set(products.map(p => p.product_type).filter(Boolean))).sort();
  }, [products]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Rechercher..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9" data-testid="catalog-search"
              />
            </div>
            <Select value={filterSupplier} onValueChange={setFilterSupplier}>
              <SelectTrigger data-testid="filter-supplier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les fournisseurs</SelectItem>
                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterOrderType} onValueChange={setFilterOrderType}>
              <SelectTrigger data-testid="filter-ordertype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types de commande</SelectItem>
                {orderTypes.map(ot => <SelectItem key={ot.id} value={ot.id}>{ot.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types de produits</SelectItem>
                {uniqueTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">
              {products.length === 0 ? "Aucun produit enregistré." : "Aucun résultat."}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader label="Fournisseur" col="supplier_name" />
                  <SortHeader label="Référence" col="reference" />
                  <SortHeader label="Nom" col="name" />
                  <SortHeader label="Type" col="product_type" />
                  <TableHead>Types commande</TableHead>
                  <SortHeader label="Prix / unité réf." col="price_per_reference_unit" />
                  <SortHeader label="Prix / usage" col="price_per_usage_unit" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(p => (
                  <TableRow key={p.id} data-testid={`catalog-row-${p.id}`}>
                    <TableCell className="font-medium">{p.supplier_name}</TableCell>
                    <TableCell className="font-mono text-xs">{p.reference || "-"}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.product_type || "-"}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(p.order_type_ids || []).map(id => {
                          const ot = orderTypes.find(o => o.id === id);
                          return ot ? <Badge key={id} variant="outline" className="text-xs">{ot.name}</Badge> : null;
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono font-semibold">
                      {p.price_per_reference_unit != null
                        ? `${formatCurrency(p.price_per_reference_unit)}/${p.reference_unit}`
                        : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell className="font-mono font-semibold text-indigo-700">
                      {p.price_per_usage_unit != null && p.consumption_per_unit
                        ? `${formatCurrency(p.price_per_usage_unit)}/${p.consumption_per_unit}`
                        : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const SuppliersPage = () => {
  const [selected, setSelected] = useState(null);
  const [dialog, setDialog] = useState({ open: false, supplier: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const validTabs = ["list", "catalog", "stock", "analytics"];
  const activeTab = validTabs.includes(tabFromUrl) ? tabFromUrl : "list";

  // Deep-link handler : /fournisseurs?open={supplier_id} ouvre directement la fiche
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || (selected && selected.id === openId)) return;
    let cancelled = false;
    api.getSupplier(openId)
      .then((res) => {
        if (cancelled) return;
        setSelected(res.data);
        // Nettoyer le param pour éviter la ré-ouverture après un retour
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          p.delete("open");
          return p;
        }, { replace: true });
      })
      .catch(() => { if (!cancelled) toast.error("Fournisseur introuvable"); });
    return () => { cancelled = true; };
  }, [searchParams, selected, setSearchParams]);

  const handleSaved = async () => {
    setDialog({ open: false, supplier: null });
    setReloadKey(k => k + 1);
    // If we were editing the currently-selected supplier, reload its fresh data
    if (selected && dialog.supplier?.id === selected.id) {
      try {
        const { data } = await api.getSupplier(selected.id);
        setSelected(data);
      } catch (_e) { /* ignore */ }
    }
  };

  if (selected) {
    return (
      <>
        <SupplierDetailView
          key={selected.id}
          supplier={selected}
          onBack={() => { setSelected(null); setReloadKey(k => k + 1); }}
          onEdit={() => setDialog({ open: true, supplier: selected })}
          onDeleted={() => { setSelected(null); setReloadKey(k => k + 1); }}
        />
        <SupplierFormDialog
          open={dialog.open}
          supplier={dialog.supplier}
          onClose={() => setDialog({ open: false, supplier: null })}
          onSaved={handleSaved}
        />
      </>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Fournisseurs</h1>
          <p className="text-muted-foreground">Gérez vos fournisseurs, produits et historiques de prix</p>
        </div>
        <Button onClick={() => setDialog({ open: true, supplier: null })} data-testid="add-supplier-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouveau fournisseur
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => {
        const next = new URLSearchParams(searchParams);
        if (v === "list") next.delete("tab"); else next.set("tab", v);
        setSearchParams(next, { replace: true });
      }}>
        <TabsList>
          <TabsTrigger value="list" data-testid="sup-main-tab-list"><Truck className="h-4 w-4 mr-1" /> Liste des fournisseurs</TabsTrigger>
          <TabsTrigger value="catalog" data-testid="sup-main-tab-catalog"><Package className="h-4 w-4 mr-1" /> Catalogue produits</TabsTrigger>
          <TabsTrigger value="stock" data-testid="sup-main-tab-stock"><Boxes className="h-4 w-4 mr-1" /> Stock & Prévisionnel</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="sup-main-tab-analytics"><TrendingUp className="h-4 w-4 mr-1" /> Analyse prix</TabsTrigger>
        </TabsList>
        <TabsContent value="list">
          <SuppliersList
            key={`list-${reloadKey}`}
            onSelect={(s) => setSelected(s)}
            onEdit={(s) => setDialog({ open: true, supplier: s })}
          />
        </TabsContent>
        <TabsContent value="catalog">
          <ProductsCatalog key={`catalog-${reloadKey}`} />
        </TabsContent>
        <TabsContent value="stock">
          <StockForecastTab key={`stock-${reloadKey}`} />
        </TabsContent>
        <TabsContent value="analytics">
          <SupplierAnalyticsPage embedded />
        </TabsContent>
      </Tabs>

      <SupplierFormDialog
        open={dialog.open}
        supplier={dialog.supplier}
        onClose={() => setDialog({ open: false, supplier: null })}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default SuppliersPage;
